import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ImagePlus, Loader2, X } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";

async function upload(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

export function ImageUpload({ value, onChange, label = "Bild", testId = "image-upload", accept = "image/*" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      onChange(await upload(file));
    } catch (e) {
      setError(errMsg(e, "Der Upload hat nicht funktioniert."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {label ? <div className="eyebrow mb-2">{label}</div> : null}
      <div className="flex items-start gap-3">
        <div className="h-20 w-28 shrink-0 overflow-hidden rounded-xl border bg-white">
          {value ? (
            <img src={fileUrl(value)} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-xs text-[var(--ink-soft)]">kein Bild</div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-ghost !px-4 !py-2 !text-xs"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid={`${testId}-button`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
            {value ? "Bild austauschen" : "Bild hochladen"}
          </button>
          {value ? (
            <button
              type="button"
              className="btn btn-ghost !px-4 !py-2 !text-xs"
              onClick={() => onChange(null)}
              data-testid={`${testId}-remove`}
            >
              <X className="h-4 w-4" /> entfernen
            </button>
          ) : null}
        </div>
      </div>
      <input ref={inputRef} type="file" accept={accept} onChange={pick} className="hidden" data-testid={`${testId}-input`} />
      {error ? <p className="mt-2 text-xs text-red-700">{error}</p> : null}
    </div>
  );
}

export function ImageGalleryUpload({ value, onChange, testId = "gallery-upload", accept = "image/*" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const images = Array.isArray(value) ? value.filter(Boolean) : [];

  const pick = async (event) => {
    const files = [...(event.target.files || [])];
    event.target.value = "";
    if (!files.length) return;
    setBusy(true);
    setError("");
    const uploaded = [];
    try {
      for (const file of files) uploaded.push(await upload(file));
      onChange([...images, ...uploaded]);
    } catch (e) {
      if (uploaded.length) onChange([...images, ...uploaded]);
      setError(errMsg(e, "Mindestens ein Bild konnte nicht hochgeladen werden."));
    } finally {
      setBusy(false);
    }
  };

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= images.length) return;
    const next = [...images];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {images.map((image, index) => (
          <div key={`${image}-${index}`} className="overflow-hidden rounded-xl border bg-white">
            <img src={fileUrl(image)} alt="" className="h-28 w-full object-cover" />
            <div className="flex items-center justify-between p-1.5">
              <div className="flex">
                <button type="button" className="btn btn-ghost !p-1.5" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Bild nach links"><ChevronLeft className="h-4 w-4" /></button>
                <button type="button" className="btn btn-ghost !p-1.5" onClick={() => move(index, 1)} disabled={index === images.length - 1} aria-label="Bild nach rechts"><ChevronRight className="h-4 w-4" /></button>
              </div>
              <button type="button" className="btn btn-ghost !p-1.5" onClick={() => onChange(images.filter((_, i) => i !== index))} aria-label="Bild entfernen"><X className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <button type="button" className="btn btn-ghost mt-3 !px-4 !py-2 !text-xs" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testId}-button`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
        Weitere Bilder hochladen
      </button>
      <input ref={inputRef} type="file" accept={accept} multiple onChange={pick} className="hidden" data-testid={`${testId}-input`} />
      {error ? <p className="mt-2 text-xs text-red-700">{error}</p> : null}
    </div>
  );
}

export default ImageUpload;
